// When a LOGGED-OUT visitor on a public workspace page types a reply/answer and
// hits Send, we stash the draft here before the "Continue with GitHub" OAuth
// round-trip. After they land back authenticated and auto-join, the post-auth
// hook reads it and PUBLISHES the reply automatically — so a fresh signup flows
// straight from "I wanted to answer this" to their answer being live, instead of
// losing the text on the redirect.
//
// Parallel to `pending-auto-join.ts` (read that first): sessionStorage (not
// localStorage) so the intent is scoped to this tab's sign-up flow and can't
// leak into a later, unrelated session. Same 10-minute TTL guards an abandoned
// OAuth flow from silently publishing on some much-later sign-in.

const KEY = "ano:pending-draft-reply";
const MAX_AGE_MS = 10 * 60_000;

/**
 * The stashed draft. `targetMessageId` is the question/message being answered
 * (a `…/m/<id>` anchor link), or null for a plain top-level channel reply.
 * `channelId` is the post target (from the public channel-content response).
 */
export interface PendingDraftReply {
  slug: string;
  channelId: string;
  channelName: string;
  targetMessageId: string | null;
  content: string;
  /** Epoch-ms stamp, set on write; used for the TTL guard. */
  ts: number;
}

/** Stash the draft before the OAuth round-trip. No-op on disabled storage. */
export function setPendingDraftReply(
  draft: Omit<PendingDraftReply, "ts">,
): void {
  // A draft with no workspace or no text is meaningless — never stash it.
  if (!draft.slug || !draft.content) return;
  try {
    const stored: PendingDraftReply = { ...draft, ts: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    /* private mode / storage disabled — the draft just won't survive auth */
  }
}

/** Parse + field-validate + TTL-check a raw stored value, else null. */
function parseValidDraft(raw: string | null): PendingDraftReply | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof p.slug !== "string" ||
      typeof p.channelId !== "string" ||
      typeof p.channelName !== "string" ||
      typeof p.content !== "string" ||
      typeof p.ts !== "number" ||
      !(p.targetMessageId === null || typeof p.targetMessageId === "string")
    ) {
      return null;
    }
    if (Date.now() - p.ts > MAX_AGE_MS) return null;
    return {
      slug: p.slug,
      channelId: p.channelId,
      channelName: p.channelName,
      targetMessageId: p.targetMessageId,
      content: p.content,
      ts: p.ts,
    };
  } catch {
    return null;
  }
}

/**
 * Read AND clear the pending draft (one-shot, so a reload can't re-publish).
 * Returns null for a missing, malformed, or stale (>10 min) draft.
 */
export function takePendingDraftReply(): PendingDraftReply | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    // Consume the slot regardless of validity — a malformed/expired blob should
    // never linger to be re-read on a later sign-in.
    sessionStorage.removeItem(KEY);
    return parseValidDraft(raw);
  } catch {
    return null;
  }
}

/** Read WITHOUT clearing — for rendering the draft back into the box pre-publish. */
export function peekPendingDraftReply(): PendingDraftReply | null {
  try {
    return parseValidDraft(sessionStorage.getItem(KEY));
  } catch {
    return null;
  }
}
